import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiVideo, FiTrendingUp, FiTag, FiTruck, FiMapPin } = FiIcons;

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: FiVideo,
      title: "Virtual Care",
      desc: "Licensed medical professionals guide your prescription plan virtually, but always personally."
    },
    {
      icon: FiTrendingUp,
      title: "Real Results",
      desc: "Join those who've achieved dramatic changes in weight, energy, and confidence."
    },
    {
      icon: FiTag,
      title: "Affordable",
      desc: "Premium care doesn't have to come at a premium price. No hidden fees, just real value."
    },
    {
      icon: FiTruck,
      title: "Fast Delivery",
      desc: "From prescription to doorstep—we ship quickly and free so you can begin without delay."
    },
    {
      icon: FiMapPin,
      title: "Straight to Your Door",
      desc: "Get your treatment delivered securely to your home. No pickups. No hassle."
    }
  ];

  return (
    <div className="pt-32 pb-20 lg:pt-40 lg:pb-32 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-sm font-bold text-teal-600 tracking-widest uppercase mb-4">About Us</h1>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-8">
              Healthcare <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">Conveniently Delivered</span>
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              At RenewRX, we believe that premium healthcare should be accessible, convenient, and personalized. Our telehealth platform connects you with licensed medical professionals who can guide your treatment plan—all from the comfort of your home.
            </p>
          </motion.div>
        </div>

        {/* Why Choose Section */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900">Why Choose RenewRX?</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="bg-white p-8 rounded-[2rem] border border-slate-100 premium-shadow hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="w-6 h-6" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h4>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mission Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-slate-900 rounded-[3rem] p-10 md:p-16 text-center relative overflow-hidden mb-16 shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/20 blur-3xl rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/20 blur-3xl rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
          
          <div className="relative z-10 max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">Our Mission</h3>
            <p className="text-xl text-teal-50 leading-relaxed opacity-90">
              We're committed to removing every obstacle so you can focus on what really matters: achieving your health and wellness goals. Our board-certified doctors are just a click away—ready to answer questions, adjust dosages at no extra cost, and provide comprehensive care that includes lab work when needed.
            </p>
          </div>
        </motion.div>

        {/* Disclaimer Note */}
        <div className="text-center max-w-4xl mx-auto">
          <p className="text-sm text-slate-500 italic leading-relaxed bg-slate-100/50 p-6 rounded-2xl border border-slate-200/50">
            Note: All medical services are referral-based and provided through licensed healthcare professionals. Prescription medications are fulfilled by Leg Up Recovery.
          </p>
        </div>

      </div>
    </div>
  );
}